import { useState, createContext, useContext, useCallback } from 'react';
import {
  effectivePeriodNumber,
  type FormsView,
  isWorkingFormsView,
} from 'utils/iapFormsView';
import {
  useCurrentPeriod,
  useWorkingPeriodNumber,
} from '../hooks/incidentHooks';
import { IAP_DASHBOARD_MODES } from '../ICSConstants';

type ICSContextType = {
  isFuture: boolean;
  setIsFuture: (isFuture: boolean) => void;
  card: string;
  setCard: (card: string) => void;
  initialResponseTab: string;
  setInitialResponseTab: (tab: string) => void;
  targetPhase: number | null;
  setTargetPhase: (phase: number | null) => void;
  /** @deprecated Prefer formsView; kept for legacy callers. */
  mode: string;
  setMode: (mode: string) => void;
  formsView: FormsView;
  setFormsView: (view: FormsView) => void;
  currentPeriodNumber: number;
  workingPeriodNumber: number;
  effectivePeriodNumber: number;
  isWorking: boolean;
  isHistorical: boolean;
  readOnlyForms: boolean;
};

const ICSContext = createContext<ICSContextType | undefined>(undefined);

/**
 * For code shared with callers that render outside the planning view, such as a
 * form widget pinned to one period and reused in a dialog. Anything that
 * actually needs the browsed view should use {@link useICSContext}.
 */
export const useOptionalICSContext = (): ICSContextType | undefined =>
  useContext(ICSContext);

export const useICSContext = (): ICSContextType => {
  const context = useOptionalICSContext();
  if (context === undefined) {
    throw new Error('useICSContext must be used within a ICSContextProvider');
  }
  return context;
};

const ICSContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { periodNumber } = useCurrentPeriod();
  const currentPeriodNumber = periodNumber ?? 0;
  const workingPeriodNumber = useWorkingPeriodNumber();

  const [isFuture, setIsFuture] = useState<boolean>(true);
  const [card, setCard] = useState<string>('');
  const [initialResponseTab, setInitialResponseTab] = useState<string>('');
  const [targetPhase, setTargetPhase] = useState<number | null>(null);
  const [formsView, setFormsViewState] = useState<FormsView>('working');

  const setFormsView = useCallback((view: FormsView) => {
    setFormsViewState(view);
    setIsFuture(isWorkingFormsView(view));
  }, []);

  const setMode = useCallback(
    (nextMode: string) => {
      if (nextMode === IAP_DASHBOARD_MODES.FUTURE) {
        setFormsView('working');
      } else {
        setFormsView(currentPeriodNumber);
      }
    },
    [currentPeriodNumber, setFormsView]
  );

  const mode = formsViewToLegacyMode(formsView);
  const isWorking = isWorkingFormsView(formsView);
  const isHistorical = !isWorking;
  const effectivePeriod = effectivePeriodNumber(formsView, workingPeriodNumber);

  // Plain object — React Compiler memoizes; avoid useMemo that fights the compiler.
  const value: ICSContextType = {
    isFuture,
    setIsFuture,
    card,
    setCard,
    initialResponseTab,
    setInitialResponseTab,
    targetPhase,
    setTargetPhase,
    mode,
    setMode,
    formsView,
    setFormsView,
    currentPeriodNumber,
    workingPeriodNumber,
    effectivePeriodNumber: effectivePeriod,
    isWorking,
    isHistorical,
    readOnlyForms: isHistorical,
  };

  return <ICSContext.Provider value={value}>{children}</ICSContext.Provider>;
};

function formsViewToLegacyMode(view: FormsView): string {
  return view === 'working'
    ? IAP_DASHBOARD_MODES.FUTURE
    : IAP_DASHBOARD_MODES.CURRENT;
}

export default ICSContextProvider;
